import React, { useMemo, useRef, useState, useEffect, Fragment } from 'react';

import { Dialog, Transition } from '@headlessui/react';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { AiOutlineRise } from 'react-icons/ai';
import { BsTelephoneFill } from 'react-icons/bs';
import { FaShare, FaUserAstronaut } from 'react-icons/fa';
import {
  HiCheck,
  HiChevronDown,
  HiClipboardCheck,
  HiClipboardCopy,
  HiOutlineTrash,
  HiPencilAlt,
} from 'react-icons/hi';
import { IoLogoWhatsapp } from 'react-icons/io';
import {
  IoChatbox,
  IoLocationOutline,
  IoSendSharp,
  IoSync,
} from 'react-icons/io5';
import { TiWarning } from 'react-icons/ti';
import {
  useBoolean,
  useCopyToClipboard,
  useEffectOnce,
  useLocalStorage,
  useWindowSize,
} from 'usehooks-ts';

import {
  deleteAd,
  GetAdLeads,
  GetAdRelatedDiscussionByMembers,
  InitiateDiscussion,
  markAdSold,
  ReportAdByUser,
  SendMessage,
  ViewAd,
} from '@/api/fetch/marketplace-api';
import {
  AdByIDRequest,
  AdViewRequest,
  Leads,
  ReportAdByUserRequest,
  ViewResponse,
} from '@/api/gen-protos/ads_pb';
import {
  GetAdRelatedDiscussionByMembersRequest,
  GetAdRelatedDiscussionByMembersResponse,
  InitiateDiscussionRequest,
  InitiateDiscussionResponse,
  NewMessageRequest,
} from '@/api/gen-protos/messenger_pb';
import { rudderstackTrackEvent } from '@/api/rudderstack/client';
import { CSRLazy } from '@/lib/CSRLazy';
import {
  categories,
  computeAdsNearestPriceRange,
  getSubCategoryById,
} from '@/lib/helpers';
import { useGoogleAdsTargeting } from '@/lib/hooks/useGoogleAdsTrageting';
import { LoadHeavyContentAtom } from '@/lib/store';
import useStorage from '@/modules/auth/hooks/useStorage';
import { genItemLink } from '@/modules/core/utils/utils';
import Button from '@/tayara-kit/Button';
import ContactForm from '@/tayara-kit/ContactForm';
import Divider, { Orientation } from '@/tayara-kit/Divider';
import SocialShare from '@/tayara-kit/SocialShare';
import Text from '@/tayara-kit/Text';

import ItemActions, { IAction } from './ItemActions';
// @ts-ignore
import ItemAnalytics from './ItemAnalytics';
import ItemAnalyticsProCard from './ItemAnalyticsProCard';
import ItemAttributes from './ItemAttributes';
import ItemDescription from './ItemDescription';
import ItemHeader from './ItemHeader';
import ItemPublisherCard from './ItemPublisherCard';
import MoreFromUser from './MoreFromUser';
import SimilarClassifieds from './SimilarClassifieds';
import { usePrependOneToLocalClassifiedsHistory } from '../../hooks/useExtendedSearch';
import useRepublishAdHook from '../../hooks/useRepublishAdHook';
import {
  AuthTokenAtom,
  UserDataAtom,
  ItemLayout,
  ItemSideGalleryLayout,
} from '../../store/grpc-free-store';
import { IAdDetailItem, IUserData, ListingState } from '../../types';
import ClicksChart from '../Charts/clickChart';
import LeadsChart from '../Charts/leadsChart';
import ReportDialog from '../reportDialog';

// @ts-ignore
const ItemChart: any = dynamic(async () => import('../Charts/ItemChart'), {
  ssr: false,
});

// @ts-ignore
const InputEmoji: any = dynamic(async () => import('react-input-emoji'), {
  ssr: false,
});

type DetailsProps = {
  adDetails: IAdDetailItem;
  userDetails: IUserData;
  countAdByUser: number;
};

const TargtedBanners = dynamic(
  async () => import('@/tayara-kit/banners/TargtedBanners'),
  {
    ssr: false,
  }
);

const BannerMobile = dynamic(
  async () => import('@/tayara-kit/banners/BannerMobile'),
  {
    ssr: false,
  }
);

const ProductBanner = dynamic(
  async () => import('@/tayara-kit/banners/ProductBanner'),
  {
    ssr: false,
  }
);

function ListingItemView({
  adDetails,
  userDetails,
  countAdByUser,
}: DetailsProps) {
  const [loadHeavyContent] = useAtom(LoadHeavyContentAtom);

  function checkos() {
    const userAgent = navigator.userAgent || navigator.vendor;
    if (/windows phone/i.test(userAgent)) {
      return 'Windows Phone';
    }

    if (/android/i.test(userAgent)) {
      return 'Android';
    }

    if (/iPad|iPhone|iPod/.test(userAgent)) {
      return 'iOS';
    }

    return 'web';
  }

  // @ts-ignore
  const track = (event) => {
    rudderstackTrackEvent(event, {
      Id: adDetails.id,
      listing_id: adDetails.id,
      title: adDetails.title,
      category: adDetails.category,
      SubCategory: categories.find(
        (sub) => sub.id === adDetails.subCategoryId || ''
      )?.name,
      price: adDetails.price,
      seller_id: adDetails.publisher.id,
      platform: checkos(),
    });
  };
  const adUrl = `www.tayara.tn/item/${adDetails.id}`;
  const [expiredIn] = useLocalStorage('expires_in', 0);
  const router = useRouter();
  const [authToken] = useAtom(AuthTokenAtom);
  // const authToken = useReadLocalStorage<string>('auth_token');
  const [showModal, setShowModal] = React.useState(false);
  const { getItem, setItem } = useStorage();
  const [, setmarkAsSold] = useState<boolean>(adDetails.sold ?? false);
  const [, setMarkAsDeleted] = useState<boolean>(adDetails.deleted ?? false);
  // const [blackList, setBlackList] = useState<string[]>();
  const [formVisible, setFormVisible] = useState(
    adDetails?.category !== 'immobilier' && adDetails?.category !== 'véhicules'
  );
  const [userData] = useAtom(UserDataAtom);
  const { value: isExpanded, toggle: toggleExpanded } = useBoolean(false);

  const { value: phoneNumberVisible, setTrue: setPhoneNumberVisibleTrue } =
    useBoolean(false);
  const showButton =
    (adDetails?.category === 'immoneuf' ||
      adDetails?.category === 'véhicules' ||
      userDetails?.kcid === '66d595fc801a64bd44e9dcfd' ||
      adDetails?.category === 'immobilier') &&
    userDetails?.isShop;
  const showButtonOnlyImmo =
    adDetails?.category === 'immoneuf' || adDetails?.category === 'immobilier';
  const {
    value: reportDialogVisible,
    setTrue: setReportDialogVisibleTrue,
    setFalse: setReportDialogVisibleFalse,
  } = useBoolean(false);
  // const {
  //   value: contactUserFormVisible,
  //   setTrue: setContactUserFormVisibleTrue,
  //   setFalse: setContactUserFormVisibleFalse,
  // } = useBoolean(false);
  const sessionid = getItem('SessionID');
  const [totalLeadsState, setTotalLeadsState] = useState(0);
  const [totalViewsState, setTotalViewsState] = useState(0);
  // @ts-ignore
  const [text, setText] = useState('');
  const [, setLeadsResponse] = useState<Leads[]>([]);
  const [viewsResponse, setViewsResponse] = useState<ViewResponse[]>([]);
  async function getViews() {
    const req = new AdByIDRequest();
    const arrayOfLeads: Leads[] = [];
    let totalLeads: number = 0;
    let totalViews: number = 0;
    req.setId(adDetails.id);
    GetAdLeads(req).then((res) => {
      const leadsList = res.getLeadsList();
      if (leadsList) {
        leadsList.forEach((lead: Leads | null) => {
          if (lead) {
            if (lead.getType() !== 0) {
              totalLeads += lead.getCount();
            } else {
              totalViews = lead.getCount();
            }
            arrayOfLeads.push(lead);
          }
        });
        setLeadsResponse(arrayOfLeads);
        setTotalLeadsState(totalLeads);
        setTotalViewsState(totalViews);
        setViewsResponse(res.getViewsList());
      }
    });
  }

  // const [leads]: any = useState({});
  // const [views]: any = useState({});

  const [endDay, startDay] = useMemo(() => {
    const d = new Date();

    return [
      Math.round(d.getTime() / 1000),
      Math.round(dayjs(d).add(-7, 'days').unix()),
    ];
  }, []);

  const isMyAd = useMemo(
    () => userData.kcid === adDetails.publisher.id,
    [adDetails.publisher.id, userData.kcid]
  );
  const trackAd = async (type: any) => {
    const viewRequest = new AdViewRequest();
    const hours = new Date().getTimezoneOffset() / 60;
    const timezone = new Date().toUTCString().substring(26);
    const TT = timezone + hours;
    // eslint-disable-next-line react-hooks/rules-of-hooks
    // @ts-ignore
    viewRequest.setSessionid(sessionid);
    viewRequest.setAdid(adDetails.id);
    viewRequest.setType(type);
    viewRequest.setLocal('fr');
    viewRequest.setTimezone(TT);
    viewRequest.setUseragent(checkos());

    await ViewAd(viewRequest);
  };

  const handleWhatsappClick = (phoneNumber: string) => {
    const url = `https://tayara.tn${genItemLink(adDetails)}`;
    const whatsappMessage = encodeURIComponent(`${url}`);
    const whatsappUrl = `https://wa.me/${phoneNumber.replace(
      /\s+/g,
      ''
    )}?text=${whatsappMessage}`;
    window.open(whatsappUrl, '_blank');
    trackAd(3);
  };

  useEffect(() => {
    console.log(adDetails);
    if (!sessionid) {
      const newSessionID = `_${Math.random().toString(36).substr(2, 9)}`;
      setItem('SessionID', newSessionID, 'session');
    }
    if (sessionid) {
      trackAd(0);
    }

    if (isMyAd && userDetails.isShop) {
      // fetch(
      //   `https://analytics.tayara.tn/listing/analytics?listing_id=${adDetails.id}&from=${startDay}&to=${endDay}`
      // )
      //   .then((res) => res.json())
      //   .then((data) => {
      //     setLeads(data.leads);
      //     setViews(data.views);
      //   })
      //   // eslint-disable-next-line no-console
      //   .catch((error) => console.error(error));
      getViews();
    }
  }, [isMyAd, adDetails.id, startDay, endDay, userDetails.isShop, sessionid]);

  const iqosId = adDetails.publisher.id;
  // @ts-ignore

  const viewsProps = {
    id: '0',
    title: 'Clics',
    value: totalViewsState,
    description: 'Personnes ont visité votre annonce',
  };

  // @ts-ignore
  const leadsProps = {
    id: '1',
    title: 'Prospects',
    value: totalLeadsState,
    description: 'Personnes sont intéressées par votre annonce',
  };

  const handleShowPhoneNumberClick = () => {
    setPhoneNumberVisibleTrue();
    track('Show Phone Number');
    trackAd(2);
  };

  // const handlePutYourNumberClick = () => {
  //   setContactUserFormVisibleTrue();
  //   track('Put Phone Number');
  // };

  const handleReportAd = () => {
    setReportDialogVisibleTrue();
  };

  function Chathandler() {
    if (Date.now() / 1000 > +expiredIn) {
      router.push({
        pathname: '/login',
        query: {
          redirect: router.asPath,
        },
      });
    } else {
      setShowModal(true);
      track('Clicked on chat');
      trackAd(1);
    }
  }
  const handleShowForm = () => {
    setFormVisible(!formVisible);
    track('clicked on contact form');
    trackAd(4);
  };
  useEffectOnce(() => {
    if (!userData.kcid) return;
    track('Visited Ad Screen');
    // getviews();

    // getUser(userData.kcid).then((user) => {
    //   const u = user.user?.blacklistList.filter((e) => e !== userData.kcid);
    //   setBlackList(u || []);
    // });
  });

  usePrependOneToLocalClassifiedsHistory(adDetails.id);

  function init() {
    if (!userData.kcid) return;

    const initiateDiscussionRequest = new InitiateDiscussionRequest();
    initiateDiscussionRequest.setAdid(adDetails.id);
    initiateDiscussionRequest.setSenderid(userData.kcid);
    initiateDiscussionRequest.setPartnerid(adDetails.publisher.id);
    initiateDiscussionRequest.setText(text);
    InitiateDiscussion(authToken, initiateDiscussionRequest).then(
      (initiateDiscussionResponse: InitiateDiscussionResponse) => {
        if (initiateDiscussionResponse.getDiscussion()?.getId()) {
          track('Message Sent');
          router.push('/chat');
        }
      },
      (reason) => {
        throw new Error(reason);
      }
    );
  }

  const markAsSold = () => {
    markAdSold(authToken, adDetails.id).then(() => {
      setmarkAsSold(true);
    });
  };
  const reportAd = (reportType: string, reportMessage: string) => {
    const reportAdByUserRequest = new ReportAdByUserRequest();
    reportAdByUserRequest.setAdid(adDetails.id);
    reportAdByUserRequest.setAdtitle(adDetails.title);
    reportAdByUserRequest.setOwneraduserphonenumber(
      adDetails?.phone || (userDetails?.phonenumber as string)
    );
    reportAdByUserRequest.setReporttype(reportType);
    reportAdByUserRequest.setReportcomment(reportMessage);

    ReportAdByUser(authToken as string, reportAdByUserRequest)
      .then(() => {
        // if (data.getStatus() === 'success') {
        // } else if (data.getStatus() === 'error') {
        // }
        setReportDialogVisibleFalse();
      })

      .catch(() => setReportDialogVisibleFalse());
  };

  const handleDeleteAd = () => {
    deleteAd(authToken, adDetails.id).then(() => {
      setMarkAsDeleted(true);
    });
  };

  const { startPostProcess } = useRepublishAdHook(adDetails);

  const handleConfirmRepublish = () => {
    startPostProcess();
  };

  const actions: IAction[] = [
    {
      label: "Modifier l'annonce",
      icon: HiPencilAlt,
      action: () => router.push(`/edit-ad/${adDetails.id}`),
      classes: '',
      requiresConfirmation: false,
      hidden: false,
    },
    {
      label: 'Marquer vendu',
      icon: HiCheck,
      action: markAsSold,
      classes:
        'btn normal-case font-bold  rounded-lg bg-transparent hover:!bg-slate-100 text-blue-500  border-none !w-fit !px-2',
      requiresConfirmation: true,
      shortLabel: 'Vendu',
      message: 'Êtes-vous sûr de marquer comme vendu?',
      hidden: false,
    },
    {
      label: 'Republier',
      icon: IoSync,
      action: handleConfirmRepublish,
      classes:
        'btn normal-case font-bold  rounded-lg bg-transparent hover:!bg-slate-100 text-blue-500  border-none !w-fit !px-2',
      requiresConfirmation: true,
      shortLabel: 'Republier',
      message: "Êtes-vous sûr de republier l'annonce ?",
      hidden: adDetails?.state === 2,
    },
    {
      label: 'Supprimer',
      icon: HiOutlineTrash,
      action: handleDeleteAd,
      classes:
        '!w-fit !text-red-400 hover:!text-red-400 bg-transparent !text-red-400 hover:bg-red-100/60 !rounded-lg',
      requiresConfirmation: true,
      shortLabel: 'Supprimer',
      message:
        "Êtes-vous sûr de Supprimer l'annonce ? cette action est irréversible.",
      hidden: false,
    },
  ];

  const action: IAction[] = [
    {
      label: "Republier l'annonce",
      icon: IoSync,
      action: handleConfirmRepublish,
      classes:
        'btn normal-case font-bold  rounded-lg bg-transparent hover:!bg-slate-100 text-blue-500  border-none !w-fit !px-2',
      requiresConfirmation: true,
      shortLabel: 'Republier',
      message: "Êtes-vous sûr de Republier l'annonce ?",
      hidden: adDetails?.state === 2,
    },
  ];

  const [messageSent, setMessageSent] = useState(false);

  function CheckAndSend() {
    if (!userData.kcid) return;

    const getAdRelatedDiscussionByMembersRequest =
      new GetAdRelatedDiscussionByMembersRequest();
    getAdRelatedDiscussionByMembersRequest.setAdid(adDetails.id);
    getAdRelatedDiscussionByMembersRequest.setFirstmemberkcid(userData.kcid);
    getAdRelatedDiscussionByMembersRequest.setSecondmemberkcid(
      adDetails.publisher.id
    );
    GetAdRelatedDiscussionByMembers(
      authToken,
      getAdRelatedDiscussionByMembersRequest
    ).then(
      (Res: GetAdRelatedDiscussionByMembersResponse) => {
        if (!userData.kcid) return;

        if (Res.getDiscussionresponse()?.getId()) {
          const newmsg = new NewMessageRequest();
          newmsg.setSenderid(userData.kcid);
          newmsg.setDiscussionid(
            Res.getDiscussionresponse()?.getId() as string
          );
          newmsg.setText(text);
          SendMessage(authToken, newmsg).then(
            (sended) => {
              if (sended) {
                track('Message Sent');
                setMessageSent(true); // Set message sent to true
              }
            },
            (reason) => {
              throw new Error(reason);
            }
          );
        } else {
          init();
        }
      },
      (reason) => {
        init();
        throw new Error(reason);
      }
    );
  }

  const [value, copy] = useCopyToClipboard();

  const windowSize = useWindowSize();

  const getListingState = (state: ListingState) => {
    switch (state) {
      case ListingState.DELETED:
        return 'supprimée';
      case ListingState.EXPIRED:
        return 'expirée';
      case ListingState.SOLD:
        return 'vendu';
      case ListingState.REJECTED:
        return 'rejetée';
      default:
        return 'active';
    }
  };

  const [layout, setLayout] = useAtom(ItemSideGalleryLayout);
  const [showContent, setShowContent] = useState(true);
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const { value: brokenImage, setValue: setBrokenImage } = useBoolean(false);
  const { value: googleAdClosed } = useBoolean(false);
  const [boosts, setBoosts] = useState<any[]>([]);
  const [, setIsLoadingBoosts] = useState(false);

  const fetchBoosts = async () => {
    setIsLoadingBoosts(true);
    try {
      const response = await fetch('/api/current-boost', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ adid: adDetails.id }),
      }).then((res) => res.json());
      if (adDetails?.level === 1) {
        response.push({ _id: 'unique-id', boosttype: 'a la une' });
      }
      setBoosts(response);

      console.log('Fetched boosts:', response);
      return response.data;
    } catch (error) {
      console.error('Error fetching boosts:', error);
      setBoosts([]);
      return [];
    } finally {
      setIsLoadingBoosts(false);
    }
  };
  useEffect(() => {
    fetchBoosts();
  }, [adDetails.id]);
  useEffect(() => {
    function shouldShowContent(
      currentScrollPos: number,
      scrollDelta: number,
      deviceWidth: number
    ) {
      const maxSmallDeviceWidth = 1024;
      const smallDeviceThreshold = 1300;
      const desktopThreshold = 650;

      if (deviceWidth < maxSmallDeviceWidth) {
        // for small devices
        return !(currentScrollPos >= smallDeviceThreshold && scrollDelta > 0);
      }
      // for desktop devices
      return !(currentScrollPos >= desktopThreshold && scrollDelta > 0);
    }

    function handleScroll() {
      const currentScrollPos = window.pageYOffset;
      const scrollDelta = currentScrollPos - prevScrollPos;
      setPrevScrollPos(currentScrollPos);

      const deviceWidth = window.innerWidth;
      const showItemActionContent = shouldShowContent(
        currentScrollPos,
        scrollDelta,
        deviceWidth
      );
      setShowContent(showItemActionContent);
    }

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [prevScrollPos]);

  const ref = useRef<null | HTMLDivElement>(null);
  // @ts-ignore
  const handleClick = () => {
    ref?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const userAvatar = userDetails.avatar || '';

  const targeting = useMemo(() => {
    const t = Object.create({});
    t.q = adDetails.title;
    t.title = adDetails.title;
    t.price = adDetails.price;
    t.priceRange = computeAdsNearestPriceRange(adDetails.price);
    t.description = adDetails.description;
    t.category = adDetails.category;
    t.subCategory = getSubCategoryById(adDetails.subCategoryId);
    adDetails.adParams?.forEach((p) => {
      if (p.label) t[p.label?.replaceAll(' ', '')] = p.value;
    });
    return t;
  }, [adDetails]);
  useGoogleAdsTargeting(targeting);
  const [expiresIn] = useState(0);

  const isAuthenticated = useMemo(
    () => Date.now() / 1000 < +(expiresIn || 0),
    [expiresIn]
  );

  return (
    <>
      {!googleAdClosed && windowSize.width > 1024 && (
        <div className="realtive w-full h-[130px] md:h-[300px] flex justify-center items-center mx-auto bg-no-repeat bg-contain bg-local bg-center md:mt-5">
          <>
            <div className="absolute  mx-auto mb-1  overflow-hidden">
              <div className="flex flex-col justify-center items-center w-full h-full">
                <span className=" text-2xl md:text-4xl font-extrabold text-neutral-200">
                  Publicité
                </span>{' '}
                <img
                  className={`w-[50px] md:w-[80px] relative -right-6 md:-right-8 transition-all duration-300 `}
                  src="/media/tayara-logo.svg"
                  alt="Logo de tayara.tn"
                />
              </div>
            </div>
          </>

          {loadHeavyContent ? (
            <TargtedBanners
              category={adDetails.category as string}
              param={{
                Scategorie: adDetails?.subCategoryId,
                Region: adDetails?.location?.delegation,
              }}
            />
          ) : (
            <></>
          )}
        </div>
      )}

      <div
        className={`grid grid-cols-12 gap-x-2 mt-6 pr-0 ${
          layout === ItemLayout.Normal ? '' : ''
        }`}
        tabIndex={0}
        onFocusCapture={() => {
          setTimeout(() => setLayout(ItemLayout.Normal), 100);
        }}
      >
        {/* Page actions, Left side */}
        <div
          className={`col-span-12 pl-4 md:pl-3 pr-6 ${
            layout === ItemLayout.Normal
              ? 'lg:pl-6 lg:col-[_span_12_/_span_9]'
              : ''
          }`}
        >
          <div
            className={` relative w-full mx-auto flex-col m-6 !py-3 bg-gray-100/50 rounded-lg border-solid border-2 border-slate-100`}
          >
            <div
              className={`w-full flex flex-col ${
                adDetails?.category !== 'immoneuf' ? 'xl:flex-row' : ''
              } items-center align-middle px-3 lg:items-start justify-between gap-2`}
            >
              <Link
                passHref
                href={
                  userDetails.isShop
                    ? `/shop/${userDetails.kcid}`
                    : `/user/${userDetails.kcid}`
                }
                className="self-center md:self-start lg:self-start w-full"
              >
                <ItemPublisherCard
                  countAdByUser={countAdByUser}
                  userDetails={userDetails}
                  showCountAds={true}
                />
              </Link>
              {/* Actions */}
              <div
                className={`flex flex-col w-full  mr-0 justify-center ${
                  layout === ItemLayout.Normal ? 'md:col-[none]' : ''
                } px-0 gap-y-1`}
              >
                {getListingState(adDetails.state) === 'active' && isMyAd && (
                  <div className="relative w-full">
                    <Link href={`/boost/${adDetails.id}`} passHref>
                      <Button
                        icon={
                          <AiOutlineRise
                            className="group-hover:animate-rocking mr-2"
                            size={30}
                          />
                        }
                        className="w-full btn-l bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-extrabold hover:from-yellow-400 hover:to-orange-400 group"
                      >
                        Bi3 Fissa3
                      </Button>
                    </Link>
                  </div>
                )}

                {/* immoneuf with shop - show only ContactForm without buttons */}
                {adDetails?.category === 'immoneuf' &&
                  !isMyAd &&
                  userDetails?.isShop && (
                    <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-sm p-6 w-full border border-gray-200/60 mb-4">
                      <div className="mb-4 pb-3 border-b border-gray-200 flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-800 mb-1">
                            Contactez le promoteur
                          </h3>
                          <p className="text-sm text-gray-600">
                            Obtenez plus d&apos;informations sur ce bien
                          </p>
                        </div>
                      </div>
                      <ContactForm
                        adid={adDetails?.id}
                        promoteur={userDetails?.fullname || ''}
                        title={adDetails?.title}
                        catId={adDetails?.category}
                        userDetails={userDetails}
                      />
                      <Button
                        aria-label="Discuter sur WhatsApp"
                        light
                        className="btn normal-case !font-bold !bg-green-500 hover:!bg-green-600 !text-white !border-solid !border-2 !border-green-500 !w-full !mt-2 !px-2"
                        onClick={() => {
                          handleWhatsappClick(
                            adDetails.phone ||
                              userDetails.phonenumber ||
                              'Erreur de copie'
                          );
                          track('WhatsApp Click');
                        }}
                        icon={
                          <IoLogoWhatsapp
                            size={22}
                            className="mr-2 text-white"
                          />
                        }
                      >
                        Discuter sur WhatsApp
                      </Button>
                    </div>
                  )}

                {/* Show buttons for: all categories except immoneuf, OR immoneuf without shop */}
                {(adDetails?.category !== 'immoneuf' || !userDetails?.isShop) &&
                  !isMyAd &&
                  iqosId !== '642fd5cc39b6f183fc7fcbbc' && (
                    <>
                      <Button
                        aria-label="Chat avec vendeur"
                        className="btn normal-case !font-bold !bg-transparent hover:!bg-slate-100 text-blue-500 !w-full !my-1 !px-2"
                        light
                        icon={<IoChatbox size={22} className="mr-2" />}
                        onClick={() => Chathandler()}
                      >
                        Chat avec le vendeur
                      </Button>
                      {userDetails?.id !== '67b5e011200960965945bfc3' && (
                        <Button
                          aria-label="Afficher numéro"
                          light
                          className="btn normal-case !font-bold !bg-green-500 hover:!bg-green-600 !text-white !w-full !my-1 !px-2"
                          onClick={() => {
                            handleWhatsappClick(
                              adDetails.phone ||
                                userDetails.phonenumber ||
                                'Erreur de copie'
                            );
                            track('WhatsApp Click');
                          }}
                          icon={
                            <IoLogoWhatsapp
                              size={22}
                              className="mr-2 text-white"
                            />
                          }
                        >
                          Discuter sur WhatsApp
                        </Button>
                      )}
                    </>
                  )}

                {/* Phone number display - for non-immoneuf OR immoneuf without shop */}
                {(adDetails?.category !== 'immoneuf' ||
                  !userDetails?.isShop) && (
                  <>
                    {phoneNumberVisible ? (
                      <div className="flex gap-2 mt-4">
                        <div className="flex-1 h-12 px-4 flex items-center bg-gradient-to-r from-gray-50 to-gray-100 font-semibold rounded-lg border border-gray-200">
                          <a
                            className="text-gray-700 hover:text-blue-600 transition-colors duration-200 flex items-center gap-2"
                            href={`tel:${
                              adDetails.phone || userDetails.phonenumber
                            }`}
                            onClick={() => {
                              track('Call Phone Number');
                            }}
                          >
                            <BsTelephoneFill size={16} />
                            {adDetails.phone || userDetails.phonenumber}
                          </a>
                        </div>
                        <button
                          className={`w-12 h-12 flex items-center justify-center rounded-lg transition-all duration-200 ${
                            value
                              ? 'bg-emerald-100 text-emerald-600 border border-emerald-200'
                              : 'bg-blue-100 text-blue-600 border border-blue-200 hover:bg-blue-200'
                          }`}
                          onClick={() => {
                            copy(
                              adDetails.phone ||
                                userDetails.phonenumber ||
                                'Erreur de copie'
                            );
                            track('Copy Phone Number');
                          }}
                        >
                          {value ? (
                            <HiClipboardCheck size={20} />
                          ) : (
                            <HiClipboardCopy size={20} />
                          )}
                        </button>
                      </div>
                    ) : (
                      <Button
                        aria-label="Afficher numéro"
                        className="btn normal-case !font-bold rounded-lg  !bg-transparent hover:!bg-slate-100 text-blue-500 !w-full !my-1 !px-2"
                        onClick={handleShowPhoneNumberClick}
                        icon={<BsTelephoneFill size={18} className="mr-2" />}
                      >
                        Afficher le numéro
                      </Button>
                    )}
                  </>
                )}

                {showButtonOnlyImmo && (
                  <Link href="/banking/simulator/">
                    <button
                      aria-label="Simulez votre crédit"
                      className="btn !w-full text-center normal-case font-medium font-sans bg-red-800 hover:bg-red-900 text-white !my-1 !px-6 flex items-center"
                    >
                      {/* <img
                        src="/public/media/t-tayara.svg"
                        alt="Icon"
                        className="h-8 w-10 mr-2"
                      /> */}
                      {'Simulez votre crédit'}
                    </button>
                  </Link>
                )}
                {showButton && adDetails?.category !== 'immoneuf' && (
                  <button
                    className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:ring-blue-500 transform text-white  font-medium px-4 py-3 mb-4 p-4 rounded-lg flex gap-2 items-center justify-center hover:bg-neuf-300 transition-colors duration-200 w-full"
                    onClick={() => handleShowForm()}
                  >
                    Nous Contacter
                    <HiChevronDown
                      size={20}
                      className={`text-white transition-all duration-200 ${
                        formVisible ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                )}
                {formVisible &&
                  showButton &&
                  adDetails?.category !== 'immoneuf' && (
                    <>
                      <div className="  inset-0  flex items-center justify-center   !z-[9999]">
                        <div className="bg-white rounded-lg shadow-xl p-6 w-full relative">
                          <ContactForm
                            adid={adDetails?.id}
                            promoteur={userDetails?.fullname || ''}
                            title={adDetails?.title}
                            catId={adDetails?.category}
                            userDetails={userDetails}
                          />
                        </div>
                      </div>
                    </>
                  )}

                {/* <Button
                  aria-label="Afficher numéro"
                  light
                  className=" !w-full btn normal-case !font-bold !bg-transparent hover:!bg-slate-100 text-blue-500 !px-2"
                  onClick={handlePutYourNumberClick}
                >
                  <span className="">حط نومروك و احنا إنكلموك</span>
                </Button> */}

                {!isMyAd && iqosId === '642fd5cc39b6f183fc7fcbbc' && (
                  <Button
                    aria-label="Afficher les adresses"
                    className="btn  !items-center normal-case !font-bold rounded-lg !border-solid !border-slate-200 !bg-transparent hover:!bg-slate-100 text-blue-500 !w-full !my-1 !px-2"
                    light
                    icon={<IoLocationOutline />}
                    onClick={() => {
                      toggleExpanded();
                      track('Show Iqos addresses');
                    }}
                  >
                    <span className="mt-[1px] mx-2 lg:block">
                      Afficher les adresses
                    </span>
                    <HiChevronDown
                      size={18}
                      className={`${isExpanded ? 'rotate-180' : ''}`}
                    />
                  </Button>
                )}
              </div>
            </div>
            <Divider
              className={`!w-[30vw] !mx-auto !mt-6 !mb-4 bg-slate-200/60`}
              orientation={Orientation.HORIZONTAL}
            />
            {/* Actions */}
            <div
              className={`flex flex-wrap gap-x-2 mt-4 justify-center items-center w-full`}
            >
              {getListingState(adDetails.state) === 'active' ? (
                <>
                  <SocialShare
                    title="Partager l'annonce"
                    shareUrl={adUrl}
                    socialIconSize={28}
                    buttonElement={
                      <Button
                        className="btn normal-case font-bold rounded-lg bg-transparent hover:!bg-slate-100 text-blue-500  border-none !w-fit !px-2"
                        icon={
                          <FaShare
                            size={18}
                            className="text-blue-500 hover:text-blue-800"
                          />
                        }
                        onClick={(e) => e.preventDefault()}
                      >
                        <span className="hidden md:hidden xl:block text-xs">
                          Partager l{"'"}annonce
                        </span>
                      </Button>
                    }
                  />
                  {isMyAd ? <ItemActions actions={actions} /> : null}
                </>
              ) : (
                <>{isMyAd ? <ItemActions actions={action} /> : null}</>
              )}
              {/* {getListingState(adDetails.state) === 'expirée' ||
                getListingState(adDetails.state) === 'supprimée' ? (
                isMyAd ? (
                ) : null
              ) : (
                <></>
              )} */}
              {/* {isMyAd && <RepublishAd adDetails={adDetails} />} */}

              <Button
                aria-label="Signaler l'annonce"
                light
                className="!w-fit !px-3 bg-transparent !text-red-400 hover:bg-red-100/60 !rounded-lg !font-bold"
                onClick={handleReportAd}
                icon={<TiWarning size={22} />}
              >
                <span className="hidden md:hidden xl:block text-xs">
                  Signaler
                </span>
              </Button>
            </div>
          </div>

          {!isMyAd && iqosId === '642fd5cc39b6f183fc7fcbbc' && (
            <div className={` w-full my-14 ${isExpanded ? '' : 'hidden'}`}>
              {/* Key vertical button */}
              <Text
                as="h2"
                text="Points de vente"
                className="text-gray-700 font-semibold text-base my-2 text-center md:text-center lg:text-start"
              />
              <div className="flex flex-col md:flex-row justify-evenly align-middle items-center">
                <img
                  src="/media/iqosTunisia.png"
                  className=""
                  alt="Tunisia's map on tayara"
                />
                <p
                  dir="auto"
                  className={`text-sm text-center md:text-start text-gray-700 font-arabic whitespace-pre-wrap	 ${
                    !isExpanded ? 'line-clamp-3' : ''
                  }`}
                >
                  <ul>
                    <li>
                      <b>IQOS Corner Tunisia Mall</b> {': '}
                      Rue de L{"'"}ile de Zembretta Tunis, 1053
                    </li>
                    <li>
                      <b>IQOS Corner Tunis City</b> {': '}
                      Tunis City Shopping Center Ariana, 2032
                    </li>
                    <li>
                      <b>IQOS Corner Tanyour Sfax</b> {': '}
                      Route Teniour, Sfax, Tunisie Sfax, 3020
                    </li>
                    <li>
                      <b>IQOS Corner Sfax Mall</b> {': '}
                      Route de Tunis, Km 10, Sakiet Ezzit, Sfax Sfax, 1080
                    </li>
                    <li>
                      <b>IQOS Corner Manar City</b> {': '}
                      Avenue Abdelaziz Saoud El Manar 3 Tunis, 2092
                    </li>
                    <li>
                      <b>IQOS Corner Mall of Sousse</b> {': '}
                      Mall Of Sousse RN1, Tunis Km 128. 4060, 4060, Tunisie
                      Sousse, 4060
                    </li>
                    <li>
                      <b>IQOS Corner Carrefour La Marsa</b> {': '}
                      Rue El Halej Tunis, 1053
                    </li>
                    <li>
                      <b>IQOS Corner Azur City</b> {': '}
                      Azur City Shopping Center Ben Arous, 2013
                    </li>
                  </ul>
                </p>
              </div>
            </div>
          )}
          {isMyAd && boosts?.length > 0 && (
            <div className="m-4">
              <p className="text-gray-700 text-md font-semibold mb-2">
                Boosts actifs pour cette annonce :
              </p>
              <div className="flex flex-wrap gap-2">
                {boosts.map(({ _id: id, boosttype }) => (
                  <div
                    key={id}
                    className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-lg"
                  >
                    {boosttype}
                  </div>
                ))}
              </div>
            </div>
          )}
          {getListingState(adDetails.state) !== 'active' && (
            <div className="w-full rounded-2xl mb-8 flex h-16 bg-red-500 text-white">
              <p className="m-auto">
                Cette annonce est {getListingState(adDetails.state)}
              </p>
            </div>
          )}
          {isMyAd && userDetails.isShop ? (
            <>
              <Text
                as="h1"
                text="Statistiques"
                dir="auto"
                className="text-gray-700 mb-4 font-bold text-2xl font-arabic"
              />
              <div
                className={`flex flex-wrap gap-2  ${
                  layout === ItemLayout.Normal ? 'md:flex-row' : ''
                } flex-col`}
              >
                <div
                  className={`relative z-0 flex flex-wrap gap-2 w-full flex-col items-center align-middle `}
                ></div>

                <ItemAnalytics adAnalytics={viewsProps} />
                <ItemAnalytics adAnalytics={leadsProps} />
              </div>
              <Divider orientation={Orientation.HORIZONTAL} />
            </>
          ) : isMyAd && !userDetails.isShop ? (
            <ItemAnalyticsProCard />
          ) : null}

          <ItemHeader adDetails={adDetails} />
          <ItemDescription adDetails={adDetails} />

          {adDetails.adParams ? (
            <>
              <ItemAttributes adParams={adDetails.adParams || []} />
            </>
          ) : null}

          {windowSize.width > 1024 && layout === ItemLayout.Normal && (
            <CSRLazy
              fallback={
                <div className="mx-auto mt-3 mb-2 h-[92px] w-[730px] rounded-xl overflow-hidden bg-neutral-100"></div>
              }
            >
              {loadHeavyContent ? (
                <ProductBanner />
              ) : (
                <div className="mx-auto mt-10 mb-2 h-[92px] w-[730px] rounded-xl overflow-hidden bg-neutral-100"></div>
              )}
            </CSRLazy>
          )}

          {userDetails.isShop ? (
            <>
              <MoreFromUser
                userData={userDetails}
                viewedClassifiedId={adDetails.id}
              />
            </>
          ) : undefined}

          <span ref={ref}> </span>

          {iqosId !== '642fd5cc39b6f183fc7fcbbc' && (
            <SimilarClassifieds
              query={adDetails.title}
              viewedClassifiedData={adDetails}
            />
          )}

          {isAuthenticated && (
            <>
              <ClicksChart viewsData={viewsResponse} />
              <LeadsChart viewsData={viewsResponse} />
            </>
          )}

          {/* {isMyAd && userDetails.isShop ? (
            <>
              {views.history &&
              views.history !== undefined &&
              views.history !== null ? (
                <div>
                  <Text
                    as="h1"
                    text="Statistiques des Clics des 7 derniers jours"
                    dir="auto"
                    className="text-gray-700 mt-8 mb-8 font-bold text-2xl font-arabic"
                  />
                  <div>
                    <CSRLazy fallback={null}>
                      <ItemChart
                        data={views.history
                          ?.sort(
                            (objA: any, objB: any) =>
                              Number(objA.time) - Number(objB.time)
                          )
                          .map((v: any) => v.value)}
                        labels={views.history
                          ?.sort(
                            (objA: any, objB: any) =>
                              Number(objA.time) - Number(objB.time)
                          )
                          .map((v: any) =>
                            dayjs(v.time * 1000).format('YYYY/MM/DD')
                          )}
                        label="Views"
                      />
                    </CSRLazy>
                  </div>
                </div>
              ) : null}

              {leads.history &&
              leads.history !== undefined &&
              leads.history !== null ? (
                <div className="mb-20">
                  <Text
                    as="h1"
                    text="Statistiques des Prospects des 7 derniers jours"
                    dir="auto"
                    className="text-gray-700 mt-8 mb-8 font-bold text-2xl font-arabic"
                  />
                  <div>
                    <CSRLazy fallback={null}>
                      <ItemChart
                        data={leads.history
                          ?.sort(
                            (objA: any, objB: any) =>
                              Number(objA.time) - Number(objB.time)
                          )
                          .map((v: any) => v.value)}
                        labels={leads.history?.map((v: any) =>
                          dayjs(v.time * 1000).format('YYYY/MM/DD')
                        )}
                        label="Leads"
                      />
                    </CSRLazy>
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <></>
          )} */}
        </div>

        {/* Page actions, Right side actions */}
      </div>
      <div
        className={`fixed flex flex-col z-[9999] top-36 lg:top-[8rem] w-full md:w-full lg:w-[65vw]  h-fit px-6 pt-8 lg:pt-2 pb-2 right-0 transition duration-300 bg-white/90 bg-blend-overlay backdrop-blur-sm  border-t-2 border-neutral-100 ${
          showContent ? '-translate-y-full' : ''
        }
        ${
          layout === ItemLayout.Wide
            ? 'lg:w-[calc(2*100vw/5.3)]'
            : 'lg:w-[calc(3*100vw/5.3)]'
        }
          `}
      >
        <div
          className={` relative w-full max-w-[1050px] mx-auto ${
            userDetails.isShop ? 'flex-col' : 'flex'
          } lg:flex-col !p-1 lg:!p-3 bg-slate-100/50 rounded-lg border-solid border-2 border-slate-100`}
        >
          <div
            className={`w-full justify-between flex  lg:flex-row         
          ${layout === ItemLayout.Wide ? 'flex-wrap gap-y-2' : ''}`}
          >
            <Link
              passHref
              href={
                userDetails.isShop
                  ? `/shop/${userDetails.kcid}`
                  : `/user/${userDetails.kcid}`
              }
            >
              <div className="w-max md:w-full flex items-center h-fit xl:w-max">
                <div className="avatar relative">
                  <div className="mask mask-squircle absolute left-[-1px] top-[-1px] w-[52px] lg:w-[72px] bg-white"></div>
                  <div className={`mask mask-squircle w-[50px] lg:w-[70px]`}>
                    {userDetails.avatar && !brokenImage ? (
                      <Image
                        loading="lazy"
                        width={90}
                        height={90}
                        className="w-full"
                        unoptimized
                        src={
                          userAvatar.includes('http')
                            ? `https://www.tayara.tn/mediaGateway/resize-image?img=${userAvatar.substring(
                                userAvatar.lastIndexOf('/') - 2
                              )}&w=100`
                            : `https://www.tayara.tn/mediaGateway/resize-image?img=${userAvatar}&w=100`
                        }
                        alt="tayara publisher profile picture"
                        onError={() => setBrokenImage(true)}
                        onLoadingComplete={(result) => {
                          if (result.naturalWidth === 0) {
                            // If the naturalWidth of the image is 0 -> Broken image
                            setBrokenImage(true);
                          }
                        }}
                      />
                    ) : (
                      <div className="bg-primary/10 text-primary p-2">
                        <FaUserAstronaut className="" size={'100%'} />
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col ml-4 mt-2">
                  {/*  Metadata */}
                  <span className={`text-2xs font-light text-gray-500`}>
                    <div className="flex-col flex  mb-3">
                      {/* Boutique data */}
                      {userDetails.isShop && (
                        <Text
                          text="Boutique"
                          className="text-xs font-normal leading-3 text-blue-500"
                        />
                      )}

                      {/* Publisher name on mobile devices */}
                      <Text
                        text={
                          userDetails.fullname?.toLowerCase() || ' sur tayara'
                        }
                        className="leading-4 text-clip mt-1 text-xs lg:text-xs font-semibold text-gray-700 capitalize"
                      />
                    </div>
                  </span>
                </div>
              </div>
            </Link>
            {/* Actions */}
            <div
              className={`w-full flex items-center justify-end ${
                layout === ItemLayout.Normal ? 'md:col-[none]' : ''
              } px-0 gap-y-1`}
            >
              {!isMyAd &&
                iqosId !== '642fd5cc39b6f183fc7fcbbc' &&
                !(
                  adDetails?.category === 'immoneuf' && userDetails?.isShop
                ) && (
                  <Button
                    aria-label="Chat avec vendeur"
                    className="btn normal-case !font-bold rounded-lg border-none lg:!border-solid lg:!border-slate-200 bg-transparent hover:!bg-slate-100 text-blue-500 !w-fit mx-0 px-0 lg:mx-2 lg:px-2"
                    light
                    icon={<IoChatbox />}
                    onClick={() => Chathandler()}
                  >
                    <span className="hidden lg:block">
                      Chat avec le vendeur
                    </span>
                  </Button>
                )}

              {/* WhatsApp button for immoneuf promoteurs */}
              {!isMyAd &&
                adDetails?.category === 'immoneuf' &&
                userDetails?.isShop && (
                  <Button
                    aria-label="Discuter sur WhatsApp"
                    light
                    className="btn normal-case !font-bold rounded-lg !border-solid !border-2 !border-green-500 !bg-green-500 hover:!bg-green-600 !text-white !w-fit mx-0 px-2 lg:mx-2 lg:px-3"
                    onClick={() => {
                      handleWhatsappClick(
                        adDetails.phone ||
                          userDetails.phonenumber ||
                          'Erreur de copie'
                      );
                      track('WhatsApp Click');
                    }}
                    icon={<IoLogoWhatsapp size={20} className="text-white" />}
                  >
                    <span className="hidden lg:block">
                      Discuter sur WhatsApp
                    </span>
                  </Button>
                )}

              {!isMyAd && iqosId === '642fd5cc39b6f183fc7fcbbc' && (
                <Button
                  aria-label="Afficher les adresses"
                  className="btn normal-case !font-bold rounded-lg border-none lg:!border-solid lg:!border-slate-200 bg-transparent hover:!bg-slate-100 text-blue-500 !w-fit mx-0 px-0 lg:mx-2 lg:px-2"
                  light
                  icon={<IoLocationOutline />}
                  onClick={() => {
                    toggleExpanded();
                    track('Show Iqos addresses');
                  }}
                >
                  <span className="hidden lg:block">Afficher les adresses</span>
                  <HiChevronDown
                    size={18}
                    className={`${isExpanded ? 'rotate-180' : ''}`}
                  />
                </Button>
              )}

              {/* TODO REFACTOR */}
              {!(adDetails?.category === 'immoneuf' && userDetails?.isShop) &&
                (phoneNumberVisible ? (
                  <div className="flex gap-1 mx-2">
                    <div
                      className={`w-full h-12 px-0 lg:px-4 items-center justify-center bg-gray-100 font-bold rounded-md  ${
                        layout === ItemLayout.Normal ? 'lg:flex' : ''
                      }`}
                    >
                      <span className="flex h-full w-full items-center gap-1 flex-row">
                        <span className="sm:mr-0.5 justify-center flex w-full gap-2 text-sm">
                          <a
                            className="text-gray-600"
                            href={`tel:${
                              adDetails.phone || userDetails.phonenumber
                            }`}
                            onClick={() => {
                              track('Call Phone Number');
                            }}
                          >
                            {adDetails.phone || userDetails.phonenumber}
                          </a>
                        </span>
                      </span>
                    </div>
                    <span
                      className={`w-12 h-12 flex-grow ${
                        layout === ItemLayout.Normal ? 'lg:flex' : ''
                      } items-center justify-center rounded-md text-blue-500 hover:text-blue-600   ${
                        value ? 'bg-emerald-100 ' : 'bg-blue-100'
                      }`}
                    >
                      {value ? (
                        <span className="flex flex-col justify-center items-center text-emerald-500 p-2">
                          <HiClipboardCheck size={19} />
                          <span className="text-3xs">Copié</span>
                        </span>
                      ) : (
                        <button
                          className="flex flex-col justify-center items-center text-blue-500 p-1"
                          onClick={() => {
                            copy(
                              adDetails.phone ||
                                userDetails.phonenumber ||
                                'Erreur de copie'
                            );
                            track('Copy Phone Number');
                          }}
                        >
                          <HiClipboardCopy size={19} />
                          <span className="text-3xs">Copier</span>
                        </button>
                      )}
                    </span>
                  </div>
                ) : (
                  <span className=" lg:block ">
                    {windowSize.width < 1024 ? (
                      <>
                        <a
                          className="btn normal-case !font-bold rounded-lg border-none lg:!border-solid lg:!border-slate-200 bg-transparent hover:!bg-slate-100 text-blue-500 !w-fit px-3 lg:mx-2"
                          href={`tel:${
                            adDetails.phone || userDetails.phonenumber
                          }`}
                          onClick={() => {
                            track('Call Phone Number');
                          }}
                        >
                          <BsTelephoneFill />
                        </a>
                      </>
                    ) : (
                      <>
                        <Button
                          aria-label="Afficher numéro"
                          light
                          className="btn normal-case !font-bold rounded-lg border-none lg:!border-solid lg:!border-slate-200 !bg-transparent hover:!bg-slate-100 text-blue-500 !w-fit mx-2"
                          onClick={handleShowPhoneNumberClick}
                          icon={<BsTelephoneFill className="mr-2" />}
                        >
                          <span className="hidden lg:block ">
                            Afficher le numéro
                          </span>
                        </Button>
                      </>
                    )}
                  </span>
                ))}
              {getListingState(adDetails.state) === 'active' && isMyAd && (
                <div className="relative w-fit h-fit">
                  <Link href={`/boost/${adDetails.id}`} passHref>
                    <Button
                      icon={
                        <AiOutlineRise
                          className="group-hover:animate-rocking"
                          size={30}
                        />
                      }
                      className="w-full p-0 md:px-2 btn-l lg:bg-gradient-to-r from-yellow-500 to-orange-500 text-orange-400 lg:text-white font-extrabold hover:from-yellow-400 hover:to-orange-400 group"
                    >
                      <span className="hidden lg:block">Bi3 Fissa3</span>
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>

          {!isMyAd && iqosId === '642fd5cc39b6f183fc7fcbbc' && (
            <div className={` w-full my-14 ${isExpanded ? '' : 'hidden'}`}>
              {/* Key vertical button */}
              <Text
                as="h2"
                text="Points de vente"
                className="text-gray-700 font-semibold text-base my-2 text-center"
              />
              <div className="flex flex-col md:flex-row justify-evenly align-middle items-center ">
                <img
                  src="/media/iqosTunisia.png"
                  className="hidden md:block"
                  alt="Tunisia's map on tayara"
                />
                <p
                  dir="auto"
                  className={`text-sm text-center md:text-start text-gray-700 font-arabic whitespace-pre-wrap	 ${
                    !isExpanded ? 'line-clamp-3' : ''
                  }`}
                >
                  <ul>
                    <li>
                      <b>IQOS Corner Tunisia Mall</b> {': '}
                      Rue de L{"'"}ile de Zembretta Tunis, 1053
                    </li>
                    <li>
                      <b>IQOS Corner Tunis City</b> {': '}
                      Tunis City Shopping Center Ariana, 2032
                    </li>
                    <li>
                      <b>IQOS Corner Tanyour Sfax</b> {': '}
                      Route Teniour, Sfax, Tunisie Sfax, 3020
                    </li>
                    <li>
                      <b>IQOS Corner Sfax Mall</b> {': '}
                      Route de Tunis, Km 10, Sakiet Ezzit, Sfax Sfax, 1080
                    </li>
                    <li>
                      <b>IQOS Corner Manar City</b> {': '}
                      Avenue Abdelaziz Saoud El Manar 3 Tunis, 2092
                    </li>
                    <li>
                      <b>IQOS Corner Mall of Sousse</b> {': '}
                      Mall Of Sousse RN1, Tunis Km 128. 4060, 4060, Tunisie
                      Sousse, 4060
                    </li>
                    <li>
                      <b>IQOS Corner Carrefour La Marsa</b> {': '}
                      Rue El Halej Tunis, 1053
                    </li>
                    <li>
                      <b>IQOS Corner Azur City</b> {': '}
                      Azur City Shopping Center Ben Arous, 2013
                    </li>
                  </ul>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {(windowSize.width <= 1024 || layout === ItemLayout.Wide) && (
        <div className="pt-6">
          <CSRLazy
            fallback={
              <div className="mx-auto mt-3 mb-2 h-[320px] w-[320px] rounded-xl overflow-hidden bg-neutral-100"></div>
            }
          >
            {loadHeavyContent ? (
              <BannerMobile />
            ) : (
              <div className="mx-auto mt-3 mb-2 h-[320px] w-[320px] rounded-xl overflow-hidden bg-neutral-100"></div>
            )}
          </CSRLazy>
        </div>
      )}
      <>
        {showModal ? (
          <Transition appear show={showModal} as={Fragment}>
            <Dialog
              as="div"
              className="relative z-10"
              onClose={() => {
                setShowModal(false);
                setMessageSent(true);
              }}
            >
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-white bg-opacity-60 backdrop-blur-md" />
              </Transition.Child>

              <div className="fixed inset-0 overflow-y-auto pb-16 lg:pb-0">
                <div className="flex w-full min-h-full  items-end justify-end p-2 lg:p-4 text-center">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                  >
                    <Dialog.Panel className="w-full  flex flex-col justify-center items-center transform overflow-hidden rounded-2xl border border-secondary/50 bg-white p-2 lg:p-6 text-left align-middle shadow-xl transition-all">
                      <div className="w-full flex justify-between items-start">
                        <div className="flex flex-row items-center">
                          <img
                            className="w-10 h-10 rounded-full"
                            src={
                              userAvatar.includes('http')
                                ? `https://www.tayara.tn/mediaGateway/resize-image?img=${userAvatar.substring(
                                    userAvatar.lastIndexOf('/') - 2
                                  )}&w=100`
                                : `https://www.tayara.tn/mediaGateway/resize-image?img=${userAvatar}&w=100`
                            }
                            alt={`Avatar of ${adDetails.publisher.name} on tayara`}
                          />
                          <div className="flex flex-col ml-4">
                            <p className="font-semibold text-xs lg:text-base text-gray-800">
                              {userDetails.fullname}
                            </p>
                            <p className="text-gray-400 text-xs lg:text-base">
                              {adDetails.phone || userDetails.phonenumber}
                            </p>
                          </div>
                        </div>
                        <div
                          className="btn btn-xs lg:btn-sm btn-circle text-gray-800 bg-gray-300"
                          onClick={() => {
                            setShowModal(false);
                            setMessageSent(false);
                          }}
                        >
                          ✕
                        </div>
                      </div>

                      <div
                        className="w-[60%] flex flex-col items-center justify-between my-5 bg-white rounded bg-center bg-contain bg-no-repeat"
                        style={{
                          backgroundImage: `url(/media/pubcover.png)`,
                        }}
                      >
                        <Dialog.Title
                          as="h3"
                          className="text-sm text-center lg:text-lg font-semibold mb-5"
                        >
                          {adDetails.title}
                        </Dialog.Title>
                        <img
                          className="w-[180px] rounded-xl"
                          src={adDetails.images[0]}
                          alt="ad details image on tayara"
                        />
                      </div>
                      {messageSent ? (
                        <div className="w-full flex flex-col items-center justify-center mb-6">
                          <p className="text-green-500 text-center text-sm lg:text-base mb-2">
                            Votre message a été envoyé avec succès à{' '}
                            {userDetails.fullname}!
                          </p>
                          <div className="flex gap-4 w-full justify-center items-center">
                            <Link
                              passHref
                              href={'/chat'}
                              aria-label="Consulter mes messages"
                              className="text-sm lg:text-base text-neutral-800 underline-offset-2 hover:underline-offset-1 underline font-semibold "
                            >
                              Consulter mes messages
                            </Link>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="mb-2 font-semibold text-xs lg:text-base text-gray-700">
                            Veuillez écrire votre message ici:
                          </p>
                          <div className="w-full flex items-center flex-wrap lg:flex-nowrap gap-2 lg:gap-0 justify-center lg:justify-between">
                            <div className="w-[96%]">
                              <CSRLazy fallback={null}>
                                <InputEmoji
                                  name="message"
                                  required
                                  value={text}
                                  onChange={setText} // Update the `text` state as the user types
                                  onEnter={() => {
                                    if (text.trim()) {
                                      // Only send if there is text
                                      CheckAndSend();
                                      setMessageSent(true);
                                    }
                                  }}
                                  cleanOnEnter
                                  placeholder="Votre message ..."
                                  className="block bg-gray-100 outline-none focus:text-gray-700"
                                />
                              </CSRLazy>
                            </div>
                            <Button
                              aria-label="Envoyer"
                              className={`w-full lg:w-fit !pl-2 !pr-2  text-white font-medium rounded ${
                                text.trim()
                                  ? 'bg-red-500 hover:bg-primary'
                                  : 'bg-gray-400 cursor-not-allowed'
                              }`}
                              onClick={() => {
                                if (text.trim()) {
                                  // Only send if there is text
                                  CheckAndSend();
                                  setMessageSent(true);
                                }
                              }}
                              disabled={!text.trim()} // Disable the button if there is no text
                            >
                              <span className="text-sm font-semibold">
                                Send
                              </span>
                              <IoSendSharp className="ml-1" />
                            </Button>
                          </div>
                        </>
                      )}
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </div>
            </Dialog>
          </Transition>
        ) : null}
      </>

      {reportDialogVisible ? (
        <Transition appear show={reportDialogVisible} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-10"
            onClose={() => setReportDialogVisibleFalse}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-white bg-opacity-50 backdrop-blur-md" />
            </Transition.Child>
            <div
              onClick={(e) => {
                if (e.target !== e.currentTarget) return;
                setShowModal(false);
                setMessageSent(false);
              }}
              className="justify-center items-center mt-24 flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none"
            >
              {' '}
              <ReportDialog
                onCLickClose={setReportDialogVisibleFalse}
                onClickSend={reportAd}
                isOpen={reportDialogVisible}
              />
            </div>
          </Dialog>
        </Transition>
      ) : (
        <></>
      )}

      {/* {contactUserFormVisible ? (
        <>
          <Transition appear show={contactUserFormVisible} as={Fragment}>
            <Dialog
              as="div"
              className="relative z-10"
              onClose={() => setContactUserFormVisibleFalse()}
            >
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-black bg-opacity-25" />
              </Transition.Child>
              <div
                onClick={(e) => {
                  if (e.target !== e.currentTarget) return;
                  setContactUserFormVisibleFalse();
                }}
                className="justify-center items-center mt-24 flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none"
              >
                <div className="flex flex-col w-11/12 sm:w-5/6 lg:w-1/2 max-w-2xl mx-auto rounded-lg  shadow-xl !z-[9999]">
                  <UserContactForm
                    onCLickClose={setContactUserFormVisibleFalse}
                  />
                </div>
                )
              </div>
            </Dialog>
          </Transition>
        </>
      ) : null} */}
    </>
  );
}

export default ListingItemView;
